import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Text } from "@/components/Primitives/Text/text"
import { QuestModal } from "@/components/Complex/QuestModal/questModal"
import { ImageCard } from "@/components/Complex/ImageCard"
import { SoonImageCard } from "@/components/Complex/SoonImageCard/soonImageCard"
import { QuestCard } from "@/components/Complex/QuestCard"
import { EmptyState } from "@/components/Primitives/EmptyState"
import { LoadingState } from "@/components/Primitives/LoadingState"
import { ErrorState } from "@/components/Primitives/ErrorState"
import { getQuestImageUrl, useQuests } from "@/hooks/useQuests"
import { useJourneys } from "@/hooks/useJourneys"
import { useWaitlists } from "@/hooks/useWaitlists"
import { useConfig } from "@/hooks/useConfig"
import { isAuthenticated } from "@/hooks/useLocalStorage"
import { toast } from "sonner"
import { Toaster } from "@/components/Primitives/Sonner"
import { useStartJourney } from "@/hooks/useStartJourney"
import { useCircles } from "@/hooks/useCircles"
import { hasQuestionsAnswered } from "@/lib/utils/questions"
import { useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "@/lib/utils/query"
import { unsubscribeFromWaitlist } from "@/lib/api/quests"
import { cn } from "@/lib/utils/utils"

export function QuestsPage() {
  const navigate = useNavigate()
  const { t } = useConfig()
  const { loading: circlesLoading, selectedCircleId } = useCircles()
  const { startJourney } = useStartJourney(selectedCircleId)
  const {
    journeys,
    loading: journeysLoading,
    error: journeysError,
    setCurrentJourneyId,
    isMature,
  } = useJourneys(isAuthenticated())
  const {
    availableQuests,
    upcomingQuests,
    loading: questsLoading,
    error: questsError,
  } = useQuests(isAuthenticated(), journeys)
  const {
    waitlists,
    loading: waitlistsLoading,
    error: waitlistsError,
  } = useWaitlists(isAuthenticated())
  const [selectedQuestId, setSelectedQuestId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.quests })
    queryClient.invalidateQueries({ queryKey: queryKeys.journeys })
    queryClient.invalidateQueries({ queryKey: queryKeys.waitlists })
  }, [queryClient])

  const renderPageContent = (content: React.ReactNode) => {
    return (
      <main className="mt-6">
        <Text variant="lead" bold>
          {t("config_quest_selection")}
        </Text>
        <Text variant="paragraph" className="mt-4">
          {t("config_quest_selection_subtitle")}
        </Text>
        {content}
      </main>
    )
  }

  if (circlesLoading) {
    return renderPageContent(
      <LoadingState message={t("config_quests_loading")} className="mt-4" />,
    )
  }

  const getQuestById = (questId: string | null) => {
    if (!questId) return null
    return availableQuests.find((quest) => quest.id === questId) || null
  }

  const handleContinueJourney = (journeyId: string) => {
    const journeyToCheck = journeys.find((journey) => journey.id === journeyId)
    if (!journeyToCheck) {
      console.error(`Unknown journedId: ${journeyId}`)
      return
    }

    const questId = journeyToCheck.quest.id

    setCurrentJourneyId(journeyId)
    if (hasQuestionsAnswered(journeyToCheck.questions)) {
      navigate(`/quests/${questId}`)
    } else {
      navigate(`/circle/${selectedCircleId}/questions?quest_id=${questId}`)
    }
  }

  const errorToastStyle =
    "!bg-orange-700 !text-white !border !border-orange-700"

  const successToastStyle = "!bg-teal-700 !text-white !border !border-teal-700"

  const unsubscribeFromQuest = async (waitlistId: string): Promise<void> => {
    try {
      await unsubscribeFromWaitlist(waitlistId)

      queryClient.invalidateQueries({ queryKey: queryKeys.waitlists })

      queryClient.invalidateQueries({ queryKey: queryKeys.journeys })

      toast.success(t("config_unsubscribe_success"), {
        className: successToastStyle,
      })
    } catch {
      toast.error(t("config_unsubscribe_error"), {
        className: errorToastStyle,
      })
    }
  }

  const subscribeToQuest = async (questId: string): Promise<void> => {
    try {
      const result = await startJourney(questId, undefined)

      if (result.statusCode === 409) {
        toast.error(t("config_notify_already_subscribed"), {
          className: errorToastStyle,
        })

        return
      } else if (result.statusCode === 500) {
        toast.error(t("config_notify_error"), {
          className: errorToastStyle,
        })

        return
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.waitlists })

      toast.success(t("config_notify_success"), {
        className: successToastStyle,
      })
    } catch {
      toast.error(t("config_notify_error"), { className: errorToastStyle })
    }
  }

  const handleNotifyQuest = async (questId: string): Promise<void> => {
    const waitlist = waitlists.find((waitlist) => waitlist.quest.id === questId)

    if (waitlist) {
      await unsubscribeFromQuest(waitlist.id)
    } else {
      await subscribeToQuest(questId)
    }
  }

  if (journeysLoading || questsLoading || waitlistsLoading) {
    return renderPageContent(
      <LoadingState message={t("config_quests_loading")} className="mt-4" />,
    )
  }

  if (journeysError || questsError || waitlistsError) {
    return renderPageContent(
      <ErrorState
        message={t("config_error_quests")}
        className="mt-4"
        onRetry={() => window.location.reload()}
      />,
    )
  }

  const selectedQuestData = selectedQuestId
    ? getQuestById(selectedQuestId)
    : null

  if (!availableQuests.length && !journeys.length) {
    return renderPageContent(
      <EmptyState message={t("config_no_quests_available")} className="mt-4" />,
    )
  }

  return renderPageContent(
    <>
      <Toaster />

      <div
        className={cn("mt-6 lg:max-w-6xl", isMature && "show-feedback-form")}
      >
        {journeys.map((journey) => {
          const buttonText =
            journey.state == "OK"
              ? t("config_button_quest_review")
              : t("config_button_quest_continue")

          return (
            <QuestCard
              key={journey.id}
              id={journey.id}
              title={journey.quest.title}
              description={journey.quest.summary}
              imageSrc={getQuestImageUrl(journey.quest.image_url)}
              imageAlt={journey.quest.title}
              className="mb-5 lg:mb-8"
              onContinue={() => handleContinueJourney(journey.id)}
              buttonText={buttonText}
              isActive={true}
            />
          )
        })}
      </div>

      <div className="lg:max-w-6xl">
        {availableQuests.map((questItem) => (
          <ImageCard
            key={questItem.id}
            title={questItem.title}
            description={questItem.summary}
            imageSrc={getQuestImageUrl(questItem.image_url)}
            imageAlt={questItem.title}
            className="mb-5 lg:mb-8"
            onPreview={() => setSelectedQuestId(questItem.id)}
            buttonText={t("config_button_quest_preview")}
          />
        ))}
      </div>

      <div className="lg:max-w-6xl">
        <div className="lg:grid lg:grid-cols-2 lg:gap-6">
          {upcomingQuests.map((quest) => {
            return (
              <SoonImageCard
                key={quest.id}
                questId={quest.id}
                title={quest.title}
                description={quest.summary}
                imageSrc={getQuestImageUrl(quest.image_url)}
                imageAlt={quest.title}
                className="lg:mb-0"
                onNotify={handleNotifyQuest}
                isSubscribed={
                  !!waitlists.find((waitlist) => waitlist.quest.id === quest.id)
                }
              />
            )
          })}
        </div>
      </div>

      {selectedQuestId && selectedQuestData && (
        <QuestModal
          isOpen={true}
          onClose={() => setSelectedQuestId(null)}
          quest={selectedQuestData}
        />
      )}
    </>,
  )
}
